import { useMemo } from 'react';
import { UI_MODE } from 'framework-listing-lib/constants';
import ListingLibUIModeSelect from 'framework-listing-lib/filters/ListingLibUIModeSelect';
import ErrorBoundary from 'framework-listing-lib/internal/ErrorBoundary';
import ClearAllFiltersButton from 'framework-listing-lib/internal/FilterBar/components/ClearAllFiltersButton';
import { FilterErrorComponent } from 'framework-listing-lib/internal/FilterBar/components/CrmListingFilterBar';
import CrmListingSearchInput from 'framework-listing-lib/internal/FilterBar/components/CrmListingSearchInput';
import { SENTRY_KEYS } from 'framework-listing-lib/utils/sentry';
import { useHasFoldersTab } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import useApplicationId from 'framework-listing-lib/internal/hooks/useApplicationId';
import { useHasSearchInput, useLegacySearchInput } from 'framework-listing-lib/internal/hooks/useSearchInputProps';
import { useIsFoldersMode, useUIModes } from 'framework-listing-lib/internal/hooks/useUIMode';

/**
 * Simulates a filter component to keep the UI aligned when there's no filters
 * to be rendered in folders mode.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function FakeFilterComponent() {
  return /*#__PURE__*/_jsx("div", {
    style: {
      height: 40
    }
  });
}
export default function FilterBarStartSlot({
  filterComponent
}) {
  const hasViews = useHasViews();
  const uiModes = useUIModes();
  const isFoldersMode = useIsFoldersMode();
  const hasFoldersTab = useHasFoldersTab();
  const hasSearchInput = useHasSearchInput();
  const hasLegacySearchInput = useLegacySearchInput();
  const applicationId = useApplicationId();
  const shouldRenderUIModeSelect = useMemo(() => {
    if (hasViews && hasFoldersTab) {
      return false;
    }

    /**
     * When @isFoldersMode is true, the UIModeSelect is rendered outside of ListingLibFilters,
     * so we don't need to render the same component twice.
     *
     * When @currentUIMode is FOLDERS
     *  - Folders list and ListingLibUIModeSelect are rendered in the sidebar.
     *  - We don't need to render ListingLibUIModeSelect on the filter bar.
     */
    if (uiModes.length === 1 || isFoldersMode) {
      return false;
    }
    return uiModes.length > 1 && uiModes.includes(UI_MODE.FOLDERS);
  }, [hasFoldersTab, hasViews, isFoldersMode, uiModes]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldRenderUIModeSelect && /*#__PURE__*/_jsx(ListingLibUIModeSelect, {}), hasLegacySearchInput && hasSearchInput && /*#__PURE__*/_jsx(CrmListingSearchInput, {}), /*#__PURE__*/_jsx(ErrorBoundary, {
      errorComponent: /*#__PURE__*/_jsx(FilterErrorComponent, {}),
      fingerprint: `${SENTRY_KEYS.CRM_LISTING_FILTERS_FILTER_COMPONENT}_${applicationId}`,
      children: filterComponent
    }), /*#__PURE__*/_jsx(ClearAllFiltersButton, {}), isFoldersMode && /*#__PURE__*/_jsx(FakeFilterComponent, {})]
  });
}